<script setup lang="ts">
import type { Presentation } from '~/models/Presentation/Presentation'
import { useRouter }  from '#imports'
import { computed, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import useSlugify from '~/composables/useSlugify'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import PresentationPageRenderer from '~/components/ResourceEngine/PresentationPageRenderer.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'

const props = withDefaults(defineProps<{
  locationId: number
  pageNumber?: number
}>(), {
  pageNumber: 1,
})

const router = useRouter()
const { findContents } = useContentApi()
const { checkItemLicense } = useLicenseControlStore()
const { slugify } = useSlugify()

const {
  data: presentation,
  isError,
  isLoading,
} = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['presentation-view', props.locationId]),
  queryFn: () => findContents<Presentation>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.Presentation],
    subtreeCriterion: [Subtree.Content, Subtree.Media],
  }, 1).then(([content]) => content ?? null),
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(presentation)

watch(presentation, () => {
  if (!presentation.value)
    return
  setTitle(presentation.value?.title)
  checkItemLicense(presentation.value)
}, { immediate: true })

const goToPage = (pageNumber: number, replace = false) => {
  const method = replace ? router.replace : router.push
  let path = router.resolve({
    name: 'presentation-page',
    params: {
      locationId: presentation.value?.locationId,
      slug: slugify(presentation.value?.title || ''),
      pageNumber,
    },
  }).href

  if (router.currentRoute.value.params?.listId) {
    path = router.resolve({
      name: 'favorite_list_navigation',
      params: { listId: router.currentRoute.value.params.listId }
    }).href + path
  }

  method(path)
}
</script>

<template>
  <NotFound v-if="isError || (!isLoading && !presentation)" />
  <PresentationPageRenderer
    v-else-if="!isLoading && presentation"
    v-show="!isLoading"
    :page-content-ids="presentation?.pages.destinationContentIds || []"
    :page-number="props.pageNumber"
    :presentation="presentation"
    @update:page-number="goToPage"
    @replace:page-number="goToPage($event, true)"
  />
  <AuSpinner v-else center />
</template>
